(ns app.front.view.search.subs
  (:require [app.domain.property.model :as property-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.dashboard.map :as dashboard-map]
            [app.front.dashboard.search :as dashboard-search]
            [app.front.entities :as entities]
            [app.front.search-counts :as search-counts]
            [app.front.view.contractor-registration.search :as contractor-registration-search]
            [app.front.view.licence.search :as licence-search]
            [app.front.view.property.dashboard.subs :as property-dashboard-subs]
            [app.front.view.property.dashboard.widgets.filters :as property-search-filters]
            [app.front.view.property.dashboard.widgets.search :as property-search]
            [app.front.view.search.complaint.search-mode :as complaint-search-mode]
            [app.front.view.search.saved-searches :as saved-searches]
            [app.front.view.workspace.search-mode :as workspace-search-mode]
            [clojure.string :as str]
            [core.front.date-util :as date-util]
            [core.front.feature-flags :as ff]
            [core.front.i18n :as i18n]
            [core.front.media-query :as media-query]
            [core.front.routing :as routing]
            [core.front.session :as session]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::state :-> ::state)

(def entity-tabs
  "Entity tabs of the search page, in the order they are shown."
  [{:id          :workspace
    :label-key   :dashboard-search/title
    :search-mode workspace-search-mode/search-mode}
   ;; The properties tab has a search of its own instead of a `dashboard-search` search mode.
   {:id        :property
    :label-key :dashboard-search/property-title}
   {:id          :licence
    :label-key   :dashboard-search/licence-title
    :search-mode licence-search/search-mode}
   {:id          :contractor-registration
    :label-key   :dashboard-search/contractor-registration-title
    :search-mode contractor-registration-search/search-mode}
   {:id          :complaint
    :label-key   :code-enforcement/complaints
    :search-mode complaint-search-mode/search-mode}])

(def entity-tab->search-mode
  (into {}
        (keep (fn [{:keys [id search-mode]}]
                (when search-mode
                  [id search-mode])))
        entity-tabs))

(def tab-title-key
  (into {} (map (juxt :id :label-key)) entity-tabs))

(defn selected-entity-tab [db]
  (get-in db [::state :selected-entity-tab] :workspace))

(rf/reg-sub ::selected-entity-tab
  :-> selected-entity-tab)

(defn fetch-overall-counts-fx [db]
  (when-not (or (session/admin? (:session/user db)) (::overall-counts db))
    {:comm/command {:command    :user/search-tab-counts
                    :data       {}
                    :on-success [::fetch-overall-counts.success]}}))

(rf/reg-event-fx ::fetch-overall-counts
  (fn [{:keys [db]} _]
    (fetch-overall-counts-fx db)))

(rf/reg-sub ::overall-counts :-> ::overall-counts)

(rf/reg-sub ::overall-count
  :<- [::overall-counts]
  :<- [:session/admin?]
  (fn [[counts admin?] [_ tab]]
    (when-not admin? (get counts tab))))

(defn tab-available? [db tab]
  (let [user (:session/user db)]
    (case tab
      (:licence :contractor-registration)
      (or (session/admin? user)
          (not= 0 (get (::overall-counts db) tab)))
      :property (session/property-management-visible-from-db? db)
      :complaint (or (session/admin? user)
                     (let [entity-db (get db ::entities/entity-db)]
                       (some (fn [group]
                               (some #{:permit-type/CE :permit-type/PWO}
                                     (->> (:group/scopes group)
                                          (map #(if (integer? %) (get entity-db %) %))
                                          (keep :scope/permit-type))))
                             (:user/authority-groups user))))
      true)))

(rf/reg-sub ::tab-available?
  (fn [db [_ tab]] (tab-available? db tab)))

(defn- unavailable-tab? [db]
  (and (:session/user db)
       (not (tab-available? db (selected-entity-tab db)))))

(rf/reg-sub ::entity-tabs-loading?
  :<- [::state]
  :<- [::dashboard-search/config-loading?]
  :<- [::property-search/loading?]
  (fn [[state config-loading? property-search-loading?] _]
    (or (:domain-groups-loading? state)
        config-loading?
        property-search-loading?)))

(rf/reg-sub ::search-term
  :<- [::state]
  :-> :search-term)

(rf/reg-sub ::mobile?
  :<- [::ff/enabled? :feature-flag.id/workspace-search-updates]
  :<- [::media-query/breakpoint-matches? :md]
  (fn [[enabled? desktop?]] (and enabled? (not desktop?))))

(defn domain-groups [db]
  (get-in db [::state :domain-groups]))

(rf/reg-sub ::domain-groups
  :-> domain-groups)

(defn domain-group-ids [db]
  (let [state         (get db ::state)
        domain-groups (domain-groups db)]
    (if (contains? state :domain-group-ids)
      (:domain-group-ids state)
      (some-> (or (when-let [branding (:branding db)]
                    (some->> domain-groups
                             (filter #(= branding (:group/uri-path-id %)))
                             first
                             :db/id))
                  (some-> domain-groups util/only :db/id))
              hash-set))))

(rf/reg-sub ::domain-group-ids
  :-> domain-group-ids)

(defn searchable-group-ids
  "Groups of the selected organizations, i.e. the groups the entity tabs are scoped to. Empty when
   no organization is selected and the search is not scoped to any of them."
  [db]
  (let [selected-ids (domain-group-ids db)]
    (into #{}
          (comp (filter #(contains? selected-ids (:db/id %)))
                (mapcat :searchable-group-ids))
          (domain-groups db))))

(defn sync-search-state [db entity-tab]
  (let [{:keys [search-term]} (get db ::state)
        group-ids             (searchable-group-ids db)
        search-data-path      [::dashboard-search/data entity-tab]]
    (cond-> db
      (str/blank? search-term)
      (p/dissoc-in (conj search-data-path ::dashboard-search/search-term))

      (not (str/blank? search-term))
      (assoc-in (conj search-data-path ::dashboard-search/search-term) search-term)

      (seq group-ids)
      (assoc-in (conj search-data-path ::dashboard-search/implicit-filter-values :group/id)
                group-ids)

      (empty? group-ids)
      (p/dissoc-in (conj search-data-path ::dashboard-search/implicit-filter-values :group/id)))))

(defn scope-authority-group-options [conditions selected-domain-group-ids searchable-group-ids]
  (if (seq selected-domain-group-ids)
    (keep (fn [{:filter.condition/keys [attribute] :as condition}]
            (if (= :group/id attribute)
              (let [values (filterv (set searchable-group-ids) (:values condition))]
                (when (seq values)
                  (assoc condition :values values)))
              condition))
          conditions)
    conditions))

(rf/reg-sub ::searchable-group-ids
  :-> searchable-group-ids)

(rf/reg-sub ::selectable-filter-conditions
  :<- [::dashboard-search/selectable-filter-conditions]
  :<- [::domain-group-ids]
  :<- [::searchable-group-ids]
  (fn [[conditions selected-domain-group-ids searchable-group-ids]]
    (scope-authority-group-options conditions selected-domain-group-ids searchable-group-ids)))

(defn property-search-group-ids
  "Domain groups the property search is scoped to: the selected organizations, or every
   organization the user can search when none of them is selected. Unlike the other tabs, the
   property search has no notion of an unscoped search, and a super admin has no property domain
   groups of their own to fall back to."
  [db]
  (or (not-empty (domain-group-ids db))
      (into #{} (map :db/id) (domain-groups db))))

(declare tab-counts-request)

(defn apply-search-fx [db]
  (let [entity-tab (selected-entity-tab db)]
    (if (= :property entity-tab)
      {:db         (search-counts/with-scope db (tab-counts-request db))
       :dispatch-n [[::property-search/search-in-groups
                     (property-search-group-ids db)
                     (get-in db [::state :search-term])]
                    [::fetch-tab-counts]]}
      {:db         (-> (sync-search-state db entity-tab)
                       (search-counts/with-scope (tab-counts-request db)))
       :dispatch-n [dashboard-search/new-search-event
                    [::fetch-tab-counts]]})))

(defn tab-counts-request
  "Request data for the result counts of the entity tabs: the organization and the search term of
   the search bar, without the filters of the tabs themselves. Every tab counts the same way,
   including the open one, so that the counts stay comparable."
  [db]
  (let [search-term (get-in db [::state :search-term])]
    (p/assoc-if {}
                :search-term (when-not (str/blank? search-term) search-term)
                :group-ids (not-empty (searchable-group-ids db))
                :domain-group-ids (not-empty (vec (sort (property-search-group-ids db)))))))

(defn fetch-tab-counts-fx
  "The counts of the tabs only depend on the search bar, so they are refetched only when it has
   changed. Switching tabs or changing the filters of a tab leaves them as they are. The previous
   counts are kept visible until the new ones arrive, like the results themselves are."
  [db]
  (let [request (tab-counts-request db)]
    (when (not= request (get-in db [::state :tab-counts-request]))
      (let [request-id (random-uuid)]
        {:db           (-> (assoc-in db [::state :tab-counts-request] request)
                           (assoc-in [::state :tab-counts-request-id] request-id)
                           (search-counts/with-scope request))
         :comm/command {:command    :user/search-tab-counts
                        :data       request
                        :on-success [::fetch-tab-counts.success request-id]}}))))

(rf/reg-event-fx ::fetch-tab-counts
  (fn [{:keys [db]} _]
    (fetch-tab-counts-fx db)))

(rf/reg-event-db ::fetch-tab-counts.success
  (fn [db [_ request-id _status {:keys [counts]}]]
    (if (= request-id (get-in db [::state :tab-counts-request-id]))
      (assoc-in db [::state :tab-counts] counts)
      db)))

(rf/reg-sub ::tab-counts
  :<- [::state]
  :-> :tab-counts)

(rf/reg-sub ::filtered-tab-counts
  :<- [::tab-counts]
  :<- [::search-counts/counts]
  (fn [[counts filtered-counts]] (merge counts filtered-counts)))

(rf/reg-sub ::submitted-search-term
  (fn [db _] (get-in db [::search-counts/scope :search-term])))

(rf/reg-event-fx ::apply-search
  (fn [{:keys [db]} _]
    (apply-search-fx db)))

(defn perform-search-fx
  "Manual search. Clears a stranded saved-search restore stash first: a stash left behind by
  a failed or older restore must not override the user's own search state. The restore flows
  never dispatch this event (they use ::apply-search), so an in-flight restore is untouched."
  [db]
  (let [db          (p/dissoc-in db [::saved-searches/state :pending-restore])
        search-term (get-in db [::state :search-term])]
    (if (= :view/search (get-in db [:route/match :data :name]))
      {:db         db
       :dispatch-n [[::apply-search]
                    [::routing/merge-query-params {:search-term search-term} :route/replace true]]}
      {:db       (-> db
                     (assoc-in [::state :pending-search?] true)
                     (assoc-in [::state :selected-entity-tab] :workspace))
       :dispatch [:route/push {:route :view/search
                               :query (p/assoc-if {}
                                                  :selected-tab "workspace"
                                                  :search-term
                                                  (when-not (str/blank? search-term)
                                                    search-term))}]})))

(rf/reg-event-fx ::perform-search
  (fn [{:keys [db]} _]
    (perform-search-fx db)))

(rf/reg-event-db ::set-search-term
  (fn [db [_ search-term]]
    (assoc-in db [::state :search-term] search-term)))

(defn select-domain-groups
  "Select domain groups and clear each entity mode's explicit group filter so the shared domain scope is authoritative."
  [db domain-group-ids]
  (reduce #(p/dissoc-in % [::dashboard-search/data %2 ::dashboard-search/filter-values :group/id])
          (assoc-in db [::state :domain-group-ids] domain-group-ids)
          (keys entity-tab->search-mode)))

(rf/reg-event-fx ::select-domain-groups
  (fn [{:keys [db]} [_ domain-group-ids]]
    {:db (-> (select-domain-groups db domain-group-ids)
             (p/dissoc-in [::saved-searches/state :pending-restore]))}))

(rf/reg-event-db ::fetch-domain-groups.success
  (fn [db [_ _status {:keys [domain-groups]}]]
    (assoc-in db [::state :domain-groups] domain-groups)))

(defn fetch-domain-groups-done-fx [db]
  (let [on-ready-events (get-in db [::state :domain-groups-on-ready])]
    (p/assoc-if {:db (update db ::state dissoc :domain-groups-loading?
                             :domain-groups-on-ready :domain-groups-on-error)}
                :dispatch-n (seq on-ready-events))))

(rf/reg-event-fx ::fetch-domain-groups.done
  (fn [{:keys [db]} _]
    (fetch-domain-groups-done-fx db)))

(rf/reg-event-fx ::fetch-domain-groups.error
  ;; Network failures skip :finally-dispatch. :on-error also replaces the default notification,
  ;; so this handler must both clear the loading state and notify the user.
  (fn [{:keys [db]} _]
    (let [on-error-events (get-in db [::state :domain-groups-on-error])]
      {:db (update db ::state dissoc :domain-groups-loading?
                   :domain-groups-on-ready :domain-groups-on-error)
       :fx (into [[:dispatch [:notifications/add :toast :error
                              :error/connection-error-title
                              :error/connection-error-description]]]
                 (map (fn [event] [:dispatch event]))
                 on-error-events)})))

(defn fetch-domain-groups-fx
  "Load or reuse searchable groups, then dispatch on-success; dispatch on-error on network failure."
  [db on-success on-error]
  (let [loading? (get-in db [::state :domain-groups-loading?])
        db       (cond-> db
                   on-success (update-in [::state :domain-groups-on-ready] (fnil conj []) on-success)
                   on-error   (update-in [::state :domain-groups-on-error] (fnil conj []) on-error))]
    (cond
      (contains? (get db ::state) :domain-groups)
      (p/assoc-if {} :dispatch on-success)

      loading?
      {:db db}

      :else
      {:comm/command {:command          :user/searchable-domain-groups
                      :on-success       [::fetch-domain-groups.success]
                      :on-error         [::fetch-domain-groups.error]
                      :finally-dispatch [::fetch-domain-groups.done]}
       :db           (assoc-in db [::state :domain-groups-loading?] true)})))

(rf/reg-event-fx ::fetch-domain-groups
  (fn [{:keys [db]} [_ on-success on-error]]
    (fetch-domain-groups-fx db on-success on-error)))

(rf/reg-event-db ::fetch-property-creation-data.success
  (fn [db [_ _status creation-data]]
    (assoc-in db [::property-dashboard-subs/initial-data :authorized-dgs] creation-data)))

(rf/reg-event-fx ::fetch-property-creation-data
  (fn [{:keys [db]} _]
    (let [user (:session/user db)]
      (when (or (property-model/user-is-property-manager? user)
                (session/admin? user))
        {:comm/command {:command    :property-management/creation-data
                        :method     :get
                        :on-success [::fetch-property-creation-data.success]}}))))

(defn- leave-dashboard-search-mode
  "Invalidate the previous mode's config and map responses before entering Property."
  [db]
  (dissoc db ::dashboard-search/search-mode ::dashboard-map/data ::dashboard-map/search-mode))

(defn init-search-fx [db]
  (let [pending-search?  (get-in db [::state :pending-search?])
        search-term      (get-in db [::state :search-term])
        entity-tab       (selected-entity-tab db)
        stash            (get-in db [::saved-searches/state :pending-restore])
        ;; Hand the restore to the tab it targets; a stash for another tab stays in place.
        pending-restore? (and stash (= (:entity-tab stash) entity-tab))
        search-event     (when (or pending-search?
                                   pending-restore?
                                   (not (str/blank? search-term)))
                           (if pending-restore?
                             [::finish-restore entity-tab]
                             [::apply-search]))
        db               (cond-> db
                           pending-search? (update ::state dissoc :pending-search?))]
    (if (= :property entity-tab)
      ;; ::apply-search fetches the tab counts for the property tab.
      (if pending-restore?
        {:db       (leave-dashboard-search-mode db)
         :dispatch [::restore-property (:op-id stash)]}
        (let [db (leave-dashboard-search-mode db)]
          {:db       (if (and (ff/enabled? :feature-flag.id/workspace-search-updates)
                              (get-in db [::state :property-initialized?]))
                       (update db ::property-search/state dissoc :selected-view)
                       (assoc-in (property-search/reset-state db) [::state :property-initialized?] true))
           :dispatch [::apply-search]}))
      (p/assoc-if
        {:dispatch-n [[::dashboard-search/init
                       (get entity-tab->search-mode entity-tab)
                       (p/assoc-if {;; The page always shows results, also for admins, who get no
                                    ;; initial search elsewhere.
                                    :always-search? true}
                                   :search-event search-event
                                   ;; Cross-page restore: hand ::init the SAVED retriggering attrs
                                   ;; so the workspace config is fetched for the restored state,
                                   ;; mirroring apply-saved-search-fx. Other tabs send none.
                                   :config-data
                                   (when (and pending-restore? (= :workspace entity-tab))
                                     (select-keys (:filter-values stash)
                                                  workspace-model/retriggering-filter-condition-attributes)))]
                      [::fetch-tab-counts]]}
        :db (when pending-search? db)))))

(defn reconcile-tab-fx [db]
  (when (unavailable-tab? db)
    (let [tab                              (selected-entity-tab db)
          cleanup-event                    (:cleanup-event (get entity-tab->search-mode tab))
          db                               (-> db
                                               (assoc-in [::state :selected-entity-tab] :workspace)
                                               (p/dissoc-in [::saved-searches/state :pending-restore]))
          {:keys [dispatch-n] :as init-fx} (init-search-fx db)]
      (-> init-fx
          (dissoc :dispatch-n)
          (assoc :db (or (:db init-fx) db)
                 :fx (cond-> (vec (:fx init-fx))
                       (:dispatch init-fx) (conj [:dispatch (:dispatch init-fx)])
                       cleanup-event (conj [:dispatch cleanup-event])
                       true (into (map (fn [event] [:dispatch event]) dispatch-n))
                       (= :view/search (get-in db [:route/match :data :name]))
                       (conj [:dispatch [::routing/merge-query-params
                                         {:selected-tab "workspace" :complaint-ref nil}
                                         :route/replace true]])))))))

(rf/reg-event-fx ::init-search
  (fn [{:keys [db]} _]
    (or (reconcile-tab-fx db) (init-search-fx db))))

(rf/reg-event-fx ::fetch-overall-counts.success
  (fn [{:keys [db]} [_ _ {:keys [counts]}]]
    (let [db (assoc db ::overall-counts counts)]
      ;; Counts and domain groups load independently. If groups arrived first, a late
      ;; zero count can hide the selected tab; reconcile it here. Otherwise, ::init-search
      ;; reconciles when domain groups are ready.
      (or (when (and (= :view/search (get-in db [:route/match :data :name]))
                     (contains? (get db ::state) :domain-groups))
            (reconcile-tab-fx db))
          {:db db}))))

(rf/reg-event-fx ::open-complaint-ref
  (fn [{:keys [db]} [_ complaint-ref]]
    (when (and (= :view/search (get-in db [:route/match :data :name]))
               (= :complaint (selected-entity-tab db))
               (tab-available? db :complaint))
      {:fx [[:dispatch [:app.front.view.search.complaint.subs/show-complaint-modal complaint-ref]]
            [:dispatch [::routing/merge-query-params {:selected-tab "complaint" :complaint-ref nil}
                        :route/replace]]]})))

(defn select-entity-tab-fx
  "Manual tab switch. Clears a stranded saved-search restore stash first: a stash left behind
  by a failed or older restore must not override the user's own tab state. The restore flows
  never dispatch this event, so an in-flight restore is untouched."
  [db entity-tab]
  (let [previous-tab       (selected-entity-tab db)
        cleanup-event      (when (not= previous-tab entity-tab)
                             (:cleanup-event (get entity-tab->search-mode previous-tab)))
        search-page-ready? (and (= :view/search (get-in db [:route/match :data :name]))
                                (contains? (get db ::state) :domain-groups))
        db                 (cond-> (-> db
                                       (assoc-in [::state :selected-entity-tab] entity-tab)
                                       (p/dissoc-in [::saved-searches/state :pending-restore]))
                             search-page-ready? (assoc-in [::state :pending-search?] true))]
    (p/assoc-if {:db db}
                :dispatch-n
                (when search-page-ready?
                  (cond-> []
                    cleanup-event (conj cleanup-event)
                    true (conj [::init-search]
                               [::routing/merge-query-params {:selected-tab (name entity-tab)}
                                :route/replace true]))))))

(rf/reg-event-fx ::select-entity-tab
  (fn [{:keys [db]} [_ entity-tab]]
    (when (or (not (:session/user db)) (tab-available? db entity-tab))
      (select-entity-tab-fx db entity-tab))))

(defn set-selected-entity-tab-fx [db entity-tab]
  ;; Query-only navigation keeps the page controller mounted. Initialize the new
  ;; search mode just as a tab click does, but ignore our own query-param updates.
  (when (not= entity-tab (selected-entity-tab db))
    (select-entity-tab-fx db entity-tab)))

(rf/reg-event-fx ::set-selected-entity-tab
  (fn [{:keys [db]} [_ entity-tab]]
    (set-selected-entity-tab-fx db entity-tab)))


;;
;; Saved-search restore
;;

(defn- current-property-restore
  "Return the pending Property restore for op-id, or nil if it is no longer current."
  [db op-id]
  (let [stash (get-in db [::saved-searches/state :pending-restore])]
    (when (and stash
               (= :property (:entity-tab stash))
               (= op-id (:op-id stash)))
      stash)))

(defn apply-property-saved-search-fx
  "Start a Property restore, or warn without changing the search if Property is unavailable."
  [db {:keys [search-term domain-group-ids property-search]}]
  (if (session/property-management-visible-from-db? db)
    (let [op-id           (random-uuid)
          on-search-page? (= :view/search (get-in db [:route/match :data :name]))
          previous-tab    (selected-entity-tab db)
          cleanup-event   (when (and on-search-page? (not= previous-tab :property))
                            (:cleanup-event (get entity-tab->search-mode previous-tab)))
          db              (-> (if (str/blank? search-term)
                                (p/dissoc-in db [::state :search-term])
                                (assoc-in db [::state :search-term] search-term))
                              (assoc-in [::state :selected-entity-tab] :property)
                              (cond-> domain-group-ids (select-domain-groups domain-group-ids))
                              property-search/reset-state
                              (assoc-in [::saved-searches/state :pending-restore]
                                        (p/assoc-if {:entity-tab      :property
                                                     :op-id           op-id
                                                     :search-term     search-term
                                                     :property-search property-search}
                                                    :domain-group-ids domain-group-ids))
                              leave-dashboard-search-mode)]
      {:db db
       :fx (cond-> []
             cleanup-event (conj [:dispatch cleanup-event])
             true (conj (if on-search-page?
                          [:dispatch [::restore-property op-id]]
                          [:dispatch [:route/push
                                      {:route :view/search
                                       :query (p/assoc-if {}
                                                          :selected-tab "property"
                                                          :search-term
                                                          (when-not (str/blank? search-term)
                                                            search-term))}]])
                        ;; Cancel delayed attribute searches before they can use the reset state.
                        [:dispatch-debounce [{:id     ::property-search-filters/attribute-filter-changed
                                              :action :cancel}]]
                        [:dispatch [::saved-searches/close-save-dialog]]))})
    {:fx [[:dispatch [:notifications/add :toast :warning :warning/warning
                      :saved-search/restore-unavailable]]]}))

(defn restore-property-fx
  "Load searchable groups for the current restore; cancel it on network failure."
  [db op-id]
  (when (current-property-restore db op-id)
    (fetch-domain-groups-fx db
                            [::restore-property.groups-loaded op-id]
                            [::restore-property.groups-failed op-id])))

(rf/reg-event-fx ::restore-property
  (fn [{:keys [db]} [_ op-id]]
    (restore-property-fx db op-id)))

(defn restore-property-groups-loaded-fx
  "Apply the accessible saved municipalities, then load their Property search config.
  Legacy saves without a scope keep the current selection."
  [db op-id]
  (when-let [{:keys [domain-group-ids search-term]} (current-property-restore db op-id)]
    (if-some [loaded-groups (get-in db [::state :domain-groups])]
      (let [loaded-group-ids         (into #{} (map :db/id) loaded-groups)
            {:keys [effective lost]} (saved-searches/resolve-restore-scope domain-group-ids
                                                                           loaded-group-ids)
            db                       (cond-> db
                                       (seq lost) (-> (select-domain-groups effective)
                                                      (assoc-in [::saved-searches/state :pending-restore
                                                                 :dropped?] true))
                                       (str/blank? search-term)       (p/dissoc-in [::state :search-term])
                                       (not (str/blank? search-term)) (assoc-in [::state :search-term] search-term))
            ;; Property requests need explicit group ids; an empty selection means all accessible groups.
            effective-group-ids      (vec (sort (property-search-group-ids db)))
            ;; Record the scope so completion can detect a municipality change during the fetch.
            db                       (-> db
                                         (assoc-in [::property-search/state :group-ids]
                                                   effective-group-ids)
                                         (assoc-in [::saved-searches/state :pending-restore :effective-scope]
                                                   effective-group-ids))]
        {:db db
         :fx [[:dispatch [::property-search/fetch-property-search-config
                          [::restore-property.finish op-id]]]]})
      ;; A malformed groups response resolves the restore as failed instead of hanging.
      {:db (p/dissoc-in db [::saved-searches/state :pending-restore])})))

(rf/reg-event-fx ::restore-property.groups-loaded
  (fn [{:keys [db]} [_ op-id]]
    (restore-property-groups-loaded-fx db op-id)))

(rf/reg-event-db ::restore-property.groups-failed
  (fn [db [_ op-id]]
    (if (current-property-restore db op-id)
      (p/dissoc-in db [::saved-searches/state :pending-restore])
      db)))

(defn restore-property-finish-fx
  "If the restore is still current, resolve and install its filters, then run one search."
  [db op-id]
  (when-some [{:keys [entity-tab property-search effective-scope] :as stash}
              (current-property-restore db op-id)]
    (when (and (= :view/search (get-in db [:route/match :data :name]))
               (= :property (selected-entity-tab db)))
      (if (not= effective-scope (get-in db [::property-search/state :group-ids]))
        {:db (p/dissoc-in db [::saved-searches/state :pending-restore])}
        (let [{:keys [states filters dropped?]}
              (saved-searches/resolve-property-snapshot
                (get-in db [::property-search/state :config])
                property-search
                (i18n/language-query db))
              term (get-in db [::state :search-term])]
          {:db (-> db
                   (assoc-in [::property-search/state :state-filter] states)
                   (assoc ::property-search-filters/attribute-filters (not-empty filters))
                   (p/dissoc-in [::saved-searches/state :pending-restore]))
           :fx (concat [[:dispatch [::apply-search]]
                        [:dispatch [::routing/merge-query-params
                                    {:search-term  (when-not (str/blank? term) term)
                                     :selected-tab (name entity-tab)}
                                    :route/replace true]]]
                       (when (or dropped? (:dropped? stash))
                         [[:dispatch [:notifications/add :toast :warning :warning/warning
                                      :saved-search/restore-warning]]]))})))))

(rf/reg-event-fx ::restore-property.finish
  (fn [{:keys [db]} [_ op-id]]
    (restore-property-finish-fx db op-id)))

(rf/reg-event-db ::clear-pending-restore
  ;; Leaving the search page drops an interrupted restore so it cannot fire on the next page.
  (fn [db _]
    (p/dissoc-in db [::saved-searches/state :pending-restore])))

(defn apply-saved-search-fx
  "Restore a saved search as one visible change, stashing the filters for ::finish-restore and
  navigating to the target tab. Property restores use apply-property-saved-search-fx."
  [db filter-entity]
  (let [{:keys [entity-tab search-term domain-group-ids filter-values property-search]}
        (saved-searches/restore-payload filter-entity)]
    (cond
      (and (:session/user db) (not (tab-available? db entity-tab)))
      {:fx [[:dispatch [:notifications/add :toast :warning :warning/warning
                        :saved-search/restore-unavailable]]]}

      (= :property entity-tab)
      (apply-property-saved-search-fx db {:search-term      search-term
                                          :domain-group-ids domain-group-ids
                                          :property-search  property-search})

      :else
      (let [on-search-page?  (= :view/search (get-in db [:route/match :data :name]))
            previous-tab     (selected-entity-tab db)
            cleanup-event    (when (and on-search-page? (not= previous-tab entity-tab))
                               (:cleanup-event (get entity-tab->search-mode previous-tab)))
            db               (-> (if (str/blank? search-term)
                                   (p/dissoc-in db [::state :search-term])
                                   (assoc-in db [::state :search-term] search-term))
                                 (assoc-in [::state :selected-entity-tab] entity-tab)
                                 (cond-> domain-group-ids (select-domain-groups domain-group-ids))
                                 (assoc-in [::saved-searches/state :pending-restore]
                                           (p/assoc-if {:entity-tab    entity-tab
                                                        :filter-values filter-values}
                                                       :domain-group-ids domain-group-ids))
                                 (cond-> (not on-search-page?) (assoc-in [::state :pending-search?] true)))
            init-opts        (p/assoc-if {:search-event   [::finish-restore entity-tab]
                                          :always-search? true}
                                         :config-data
                                         (when (= :workspace entity-tab)
                                           (select-keys filter-values
                                                        workspace-model/retriggering-filter-condition-attributes)))
            navigation-event (if on-search-page?
                               [::dashboard-search/init
                                (get entity-tab->search-mode entity-tab)
                                init-opts]
                               [:route/push
                                {:route :view/search
                                 :query (p/assoc-if
                                          {}
                                          :selected-tab (name entity-tab)
                                          :search-term (when-not (str/blank? search-term)
                                                         search-term))}])]
        {:db db
         :fx (cond-> []
               cleanup-event (conj [:dispatch cleanup-event])
               true (conj [:dispatch navigation-event]
                          [:dispatch [::saved-searches/close-save-dialog]]))}))))

(rf/reg-event-fx ::apply-saved-search
  (fn [{:keys [db]} [_ filter-entity]]
    (apply-saved-search-fx db filter-entity)))

(defn- reconcile-legacy-authority-group-scope
  "Legacy saves have no organization scope to restore. Keep their authority groups selectable
  by clearing a conflicting current organization scope rather than dropping authority groups."
  [db saved-domain-group-ids filter-values]
  (if (and (nil? saved-domain-group-ids)
           (seq (domain-group-ids db))
           (some (complement (searchable-group-ids db)) (:group/id filter-values)))
    (select-domain-groups db #{})
    db))

(defn finish-restore-fx
  "Apply the pending restore when it targets the tab whose configuration has loaded. Missing
  saved values are omitted. Saved Organizations that no longer resolve are removed while the
  surviving ones are kept; only when every saved Organization is lost does the shared
  selection become unrestricted."
  [db target-tab]
  (when-let [{:keys [entity-tab filter-values domain-group-ids]} (get-in db [::saved-searches/state :pending-restore])]
    (when (= target-tab entity-tab)
      (when-let [config (get-in db [::dashboard-search/data target-tab ::dashboard-search/search-config])]
        (cond
          ;; A search + URL merge would pollute the page the user navigated to; drop the restore.
          (not= :view/search (get-in db [:route/match :data :name]))
          {:db (-> db
                   (p/dissoc-in [::saved-searches/state :pending-restore])
                   (p/dissoc-in [::state :pending-search?]))}

          ;; On a direct page load, saved searches can arrive before domain groups. Keep the
          ;; restore pending until ::fetch-domain-groups.done continues through ::init-search.
          (contains? (get db ::state) :domain-groups)
          (let [search-mode                 (get entity-tab->search-mode target-tab)
                {:keys [resolved dropped?]} (saved-searches/resolve-available-values
                                              config
                                              filter-values
                                              (:filter-value search-mode))
                fv-path                     [::dashboard-search/data target-tab ::dashboard-search/filter-values]
                loaded-group-ids            (into #{} (map :db/id) (domain-groups db))
                {:keys [effective lost]}    (saved-searches/resolve-restore-scope domain-group-ids
                                                                                  loaded-group-ids)
                db                          (-> db
                                                (cond-> (seq lost) (select-domain-groups effective))
                                                (reconcile-legacy-authority-group-scope domain-group-ids resolved)
                                                (cond-> (seq resolved) (assoc-in fv-path resolved)
                                                        (empty? resolved) (p/dissoc-in fv-path))
                                                dashboard-search/update-revision
                                                (p/dissoc-in [::saved-searches/state :pending-restore])
                                                (p/dissoc-in [::state :pending-search?]))
                term                        (get-in db [::state :search-term])]
            {:db db
             :fx (concat [[:dispatch [::apply-search]]]
                         [[:dispatch [::routing/merge-query-params
                                      {:search-term  (when-not (str/blank? term) term)
                                       :selected-tab (name target-tab)}
                                      :route/replace true]]]
                         (when (or dropped? (seq lost))
                           [[:dispatch [:notifications/add :toast :warning :warning/warning
                                        :saved-search/restore-warning]]]))}))))))

(rf/reg-event-fx ::finish-restore
  (fn [{:keys [db]} [_ target-tab]]
    (finish-restore-fx db target-tab)))

;;
;; Save-data for the save button and dialog
;;

(defn- save-filter-values [filter-values]
  (into {} (filter (comp seq val)) filter-values))

(rf/reg-sub ::save-data
  :<- [::selected-entity-tab]
  :<- [::search-term]
  :<- [::domain-group-ids]
  :<- [::dashboard-search/filter-values]
  :<- [::dashboard-search/search-config]
  :<- [::dashboard-search/val->str-fn]
  :<- [::dashboard-search/search-mode]
  :<- [::property-search/state-filter]
  :<- [::property-search-filters/attribute-filters]
  :<- [::property-search/property-attribute-templates]
  :<- [:language]
  :<- [:tr]
  :<- [::date-util/date-fn]
  :<- [::ff/enabled-flags]
  (fn [[entity-tab search-term domain-group-ids filter-values {:keys [default-selected-values]}
        val->str-fn search-mode state-filter attribute-filters templates lang tr format-date enabled-flags] _]
    (p/assoc-if {:entity-tab            entity-tab
                 :search-term           search-term
                 :domain-group-ids      domain-group-ids
                 :filter-values         (save-filter-values filter-values)
                 :default-filter-values (save-filter-values default-selected-values)
                 :val->str-fn           val->str-fn
                 :attribute-order       (saved-searches/description-attribute-order search-mode enabled-flags)
                 :lang                  lang
                 :tr                    tr
                 :format-date           format-date
                 :templates             templates}
                :property-search   (when (= :property entity-tab)
                                     (saved-searches/property-snapshot
                                       state-filter templates lang attribute-filters))
                :state-filter      (when (= :property entity-tab) state-filter)
                :attribute-filters (when (= :property entity-tab) attribute-filters))))

(rf/reg-sub ::save-enabled?
  :<- [::save-data]
  :-> saved-searches/save-enabled?)

;;
;; Saved-search dropdown rows
;;

(defn- tab-labels [tr]
  (into {}
        (map (fn [{:keys [id label-key]}]
               [id (tr [label-key])]))
        entity-tabs))

(rf/reg-sub ::saved-search-rows
  :<- [::saved-searches/saved-searches]
  :<- [::saved-searches/labels]
  :<- [::state]
  :<- [:language]
  :<- [:tr]
  :<- [::date-util/date-fn]
  :<- [::ff/enabled-flags]
  (fn [[saved-searches labels state lang tr format-date enabled-flags] _]
    (let [tab-labels  (tab-labels tr)
          format-opts {:tr          tr
                       :format-date format-date
                       ;; The saved Property values are named with the fetched template labels.
                       :lang        lang
                       :templates   (:property-attribute-templates labels)
                       :label-fns   (saved-searches/stored-label-fns
                                      entity-tab->search-mode
                                      labels
                                      tr
                                      lang
                                      enabled-flags)}]
      (mapv #(saved-searches/saved-search->row (:domain-groups state) tab-labels % format-opts)
            saved-searches))))

(rf/reg-event-fx ::init
  (fn [_ _]
    {:fx [[:dispatch [::session/wait-for-user-data [::fetch-overall-counts]]]
          [:dispatch [::fetch-domain-groups [::session/wait-for-user-data [::init-search]]]]
          [:dispatch [::saved-searches/fetch-unified-filters]]
          [:dispatch [::session/wait-for-user-data [::fetch-property-creation-data]]]]}))

(rf/reg-event-fx ::cleanup
  (fn [{:keys [db]} _]
    (p/assoc-if {:db (update db ::state dissoc :property-initialized?)}
                :dispatch (:cleanup-event (get entity-tab->search-mode (selected-entity-tab db))))))

(rf/reg-event-fx ::clear-mobile-filters
  (fn [{:keys [db]} _]
    (if (= :property (selected-entity-tab db))
      {:db       (-> db
                     (dissoc ::property-search-filters/attribute-filters)
                     (assoc-in [::property-search/state :state-filter] #{})
                     (assoc-in [::property-search/state :sort-condition]
                               (get-in db [::property-search/state :config :sort-condition])))
       :dispatch [::property-search/search]}
      {:db       (-> db
                     (assoc-in (dashboard-search/data-path db ::dashboard-search/filter-values) {})
                     (assoc-in (dashboard-search/data-path db :sort-condition)
                               (:sort-condition (dashboard-search/get-data db ::dashboard-search/search-config)))
                     dashboard-search/update-revision)
       :dispatch (if (and (dashboard-search/workspace-search? db)
                          (ff/enabled? :feature-flag.id/dashboard-search-functions))
                   [::dashboard-search/fetch-search-filter-values]
                   dashboard-search/new-search-event)})))

(rf/reg-event-fx ::set-property-sort
  (fn [{:keys [db]} [_ sort-condition]]
    {:db       (assoc-in db [::property-search/state :sort-condition] sort-condition)
     :dispatch [::property-search/search]}))
